import { Drawer } from 'antd'
import { FormCard } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { Spinner } from '@lmc/ui-kit'
import {
  OverrideLevel,
  PaymentCard,
  useGetPaymentCardHistory
} from '../../hooks/usePaymentCards'
import { formatRateDisplay } from './PayRateCard'
import { useShiftTypesList } from '../../hooks/useShiftTypesList'
import { useVisitTypesList } from '../../hooks/useVisitTypesList'
import { useMemo } from 'react'
import { DATE_FORMAT, TIME_FORMAT } from '../../utils.datetime'

type PayRateHistoryDrawerProps = {
  cardId?: string
  cardName: string
  open: boolean
  onClose: () => void
}

const PayRateHistoryDrawer = ({
  cardId,
  cardName,
  open,
  onClose
}: PayRateHistoryDrawerProps) => {
  const { t } = useTranslation()
  const { data: history, isLoading } = useGetPaymentCardHistory(
    open ? cardId : undefined
  )
  const { shiftTypes } = useShiftTypesList()
  const { visitTypes } = useVisitTypesList()

  const eventTypeNameMap = useMemo<Record<string, string>>(() => {
    const allEventTypes = [
      ...(shiftTypes || [])
        .filter((st) => st.id)
        .map((st) => ({ id: st.id!, name: st.name })),
      ...(visitTypes || [])
        .filter((vt) => vt.id)
        .map((vt) => ({ id: vt.id!, name: vt.name }))
    ]
    return Object.fromEntries(allEventTypes.map((et) => [et.id, et.name]))
  }, [shiftTypes, visitTypes])

  return (
    <StyledDrawer
      title={t('RATE_VERSION_HISTORY', { name: cardName })}
      open={open}
      onClose={onClose}
      width={560}
      destroyOnClose
    >
      {isLoading && <Spinner />}

      {!isLoading &&
        history?.map((card) => (
          <HistoryCard
            key={card._id}
            card={card}
            eventTypeNameMap={eventTypeNameMap}
          />
        ))}
    </StyledDrawer>
  )
}

const HistoryCard = ({
  card,
  eventTypeNameMap
}: {
  card: PaymentCard
  eventTypeNameMap: Record<string, string>
}) => {
  const { t } = useTranslation()
  const baseRate = card.rates.find((r) => r.level === OverrideLevel.BASE)
  const overrides = card.rates.filter((r) => r.level !== OverrideLevel.BASE)

  const effectiveFromDisplay = card.effectiveFrom
    ? `${dayjs(card.effectiveFrom).format(DATE_FORMAT)}  ${dayjs(card.effectiveFrom).format(TIME_FORMAT)}`
    : '-'

  return (
    <StyledFormCard level={2} title={card.name}>
      <ViewSection>
        <ViewLabel level={2}>{t('EFFECTIVE_FROM')}</ViewLabel>
        <ViewValue>{effectiveFromDisplay}</ViewValue>
      </ViewSection>

      <RatesRow>
        <ViewSection>
          <ViewLabel level={2}>{t('BASE_RATE')}</ViewLabel>
          <ViewValue>
            {baseRate ? formatRateDisplay(baseRate.rate, baseRate.rateType, t) : '-'}
          </ViewValue>
        </ViewSection>

        {overrides.map((override) => (
          <ViewSection key={override.eventTypeId}>
            <ViewLabel level={2}>
              {eventTypeNameMap[override.eventTypeId!] || override.eventTypeId}
            </ViewLabel>
            <ViewValue>{formatRateDisplay(override.rate, override.rateType, t)}</ViewValue>
          </ViewSection>
        ))}
      </RatesRow>
    </StyledFormCard>
  )
}

const StyledDrawer = styled(Drawer)`
  .ant-drawer-header-title {
    flex-direction: row-reverse;
  }
`

const StyledFormCard = styled(FormCard)`
  margin-bottom: 16px;

  > div:last-child {
    gap: 16px;
  }
`

const RatesRow = styled.div`
  display: flex;
  gap: 48px;
  flex-wrap: wrap;
`

const ViewSection = styled.div``

const ViewLabel = styled(Text)`
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.color.neutral.color5};
  font-weight: 500;
`

const ViewValue = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

export default PayRateHistoryDrawer
